import { CircularProgress, Stack } from '@mui/joy'
import { useGate, useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { carModel } from '~/entities/common/Car'
import { documentsModel } from '~/entities/common/Car/Documents'
import { usePermissions } from '~/entities/common/viewer'
import { carEditModel } from '~/features/common/Сars/EditCar'
import { Gate } from '~/features/common/Сars/EditCar/model'
import { mainStatusModel } from '~/features/common/Сars/ui/MainStatus'
import { Status } from '~/features/common/Сars/ui/Status'
import {
  ButtonInspectionExtra,
  StatusAlert,
  useConfigTabs,
} from '~/pages/common/Cars/EditCar'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import RoutesTabs from '~/shared/ui/RoutesTabs'
import { AuditsModal } from '~/widgets/common/AuditsModal'
import { DocumentsPageAfrica } from '../Documents'

export const CarPageAfrica = () => {
  const { t } = useTranslation()

  const { canAuditsView } = usePermissions()
  const params = useParams<{ carId: string }>()
  const carId = params.carId as string

  useGate(Gate, { id: carId })
  useGate(documentsModel.DocumentsGate)
  useGate(mainStatusModel.Gate)

  const isLoading = useStore(carModel.requestFx.pending)
  const configTabs = useConfigTabs({ documentsPage: <DocumentsPageAfrica /> })

  const carVin = useStoreMap(carEditModel.$car, (car) => car?.getVin())
  const carPlateNumber = useStoreMap(carEditModel.$car, (car) =>
    car?.getPlateNumber(),
  )
  const carTitle = `${carVin} [${carPlateNumber || '-'}]`

  const title = `${t('car')} ${isLoading ? '...' : carTitle}`

  const breadcrumbs = useMemo(
    (): BreadcrumbsList => [
      {
        titleI18n: 'cars',
        link: '/cars',
      },
      { title: carTitle || '' },
    ],
    [carTitle],
  )

  const carError = carModel.useCarError(carId)
  if (carError) {
    return <NotFound message={t('alert.failed to load vehicle')} />
  }

  return (
    <PageContainer title={title}>
      <RoutesTabs
        parentPath={`/cars/${carId}`}
        config={configTabs}
        parentContent={
          <Container isHeader>
            <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />

            <Status carId={carId} />

            <Stack
              direction='row'
              alignItems='end'
              justifyContent='space-between'
            >
              <PageTitle loading={isLoading}>{carTitle}</PageTitle>
              {isLoading && <CircularProgress size='sm' />}
              {!isLoading && (
                <Stack direction='row' spacing={2}>
                  {canAuditsView && <AuditsModal auditableId={carId} />}
                  <ButtonInspectionExtra />
                </Stack>
              )}
            </Stack>
            <br />
            <StatusAlert />
          </Container>
        }
      />
    </PageContainer>
  )
}
